<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'select']);

const { t } = useI18n();
const store = useStore();
const cannedResponses = useMapGetter('getCannedResponses');

const searchInput = ref(null);
const listContainerRef = ref(null);
const selectedIndex = ref(0);
const searchKey = ref('');
const noop = () => {};

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    if (!value) {
      emit('close');
    }
  },
});

const items = computed(() => {
  return cannedResponses.value.map(response => ({
    id: response.id,
    shortCode: response.short_code,
    content: response.content,
  }));
});

const navigableItems = computed(() => {
  return props.show ? items.value : [];
});

const selectedItem = computed(() => {
  return items.value[selectedIndex.value] || null;
});

const fetchCannedResponses = async () => {
  await store.dispatch('getCannedResponse', { searchKey: searchKey.value });
};

const focusSearchInput = () => {
  nextTick(() => {
    searchInput.value?.focus();
    searchInput.value?.select();
  });
};

const adjustScroll = () => {
  nextTick(() => {
    const container = listContainerRef.value;
    if (!container) return;

    const selectedElement = container.querySelector(
      `#canned-response-item-${selectedIndex.value}`
    );
    selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

const handleSelect = () => {
  if (!selectedItem.value) return;
  emit('select', selectedItem.value);
};

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  handleSelect();
};

useKeyboardNavigableList({
  items: navigableItems,
  onSelect: handleSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => props.show,
  async visible => {
    if (!visible) return;
    searchKey.value = '';
    selectedIndex.value = 0;
    await fetchCannedResponses();
    focusSearchInput();
  }
);

watch(searchKey, async () => {
  if (!props.show) return;
  await fetchCannedResponses();
});

watch(items, newItems => {
  if (!newItems.length) {
    selectedIndex.value = 0;
    return;
  }

  if (selectedIndex.value > newItems.length - 1) {
    selectedIndex.value = 0;
  }
});
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="noop"
    size="medium"
    show-close-button
  >
    <div class="flex flex-col h-[34rem]">
      <div class="flex flex-col gap-4 border-b border-n-weak px-6 py-7">
        <div class="flex flex-col gap-2">
          <h3 class="mb-0 text-xl font-semibold tracking-tight text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.TITLE') }}
          </h3>
          <p class="mb-0 max-w-2xl text-sm leading-6 text-n-slate-10">
            {{ $t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.DESCRIPTION') }}
          </p>
        </div>
        <div class="pt-1">
          <input
            ref="searchInput"
            v-model="searchKey"
            type="text"
            class="w-full rounded-2xl border border-n-weak bg-n-solid-1 px-5 py-4 text-base leading-6 text-n-slate-12 shadow-sm outline-none transition placeholder:text-n-slate-10 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            :placeholder="
              $t(
                'CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.SEARCH_PLACEHOLDER'
              )
            "
          />
        </div>
      </div>
      <div class="grid min-h-0 flex-1 grid-cols-[16rem_minmax(0,1fr)]">
        <div class="border-r border-n-weak bg-n-slate-2/30 px-2 py-2">
          <div
            ref="listContainerRef"
            class="h-full overflow-y-auto rounded-xl bg-n-solid-1 p-1"
          >
            <button
              v-for="(item, index) in items"
              :id="`canned-response-item-${index}`"
              :key="item.id"
              type="button"
              class="mb-1 flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition last:mb-0"
              :class="
                index === selectedIndex
                  ? 'bg-n-alpha-black2 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-black2'
              "
              @click="onListItemSelection(index)"
              @mouseover="onHover(index)"
            >
              <span class="truncate font-medium">
                {{ item.shortCode }}
              </span>
            </button>
            <div
              v-if="!items.length"
              class="flex h-full items-center justify-center px-4 text-center text-sm text-n-slate-10"
            >
              {{ $t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.EMPTY') }}
            </div>
          </div>
        </div>
        <div class="flex min-h-0 flex-col px-6 py-5">
          <template v-if="selectedItem">
            <h4
              class="mb-3 text-2xl font-semibold leading-tight text-n-slate-12"
            >
              {{ selectedItem.shortCode }}
            </h4>
            <div
              class="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words text-base leading-7 text-n-slate-11"
            >
              {{ selectedItem.content }}
            </div>
          </template>
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-n-slate-10"
          >
            {{ $t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.EMPTY') }}
          </div>
        </div>
      </div>
      <div
        class="flex items-center gap-4 border-t border-n-weak px-6 py-3 text-xs text-n-slate-10"
      >
        <span>
          {{ t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.KEYBOARD_NAV') }}
        </span>
        <span>
          {{
            t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.KEYBOARD_SELECT')
          }}
        </span>
        <span>
          {{ t('CONVERSATION.REPLYBOX.CANNED_RESPONSES_PANEL.KEYBOARD_CLOSE') }}
        </span>
      </div>
    </div>
  </woot-modal>
</template>
